import { CustomFieldInput } from '@/components/customField/customFieldInput';
import { InputField } from '@/components/form/fields/InputField';
import {
  ISelectOption,
  SelectField,
} from '@/components/form/fields/SelectField';
import { SelectMultipleField } from '@/components/form/fields/SelectMultipleField';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import {
  moduleTypeOptions,
  customFieldType,
  ModuleType,
} from '@/utils/constants/customField';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FaPlus } from 'react-icons/fa';
import { FaMinus } from 'react-icons/fa6';
import { CustomFieldOption } from './CustomFieldOption';

type ICustomFieldProps = {
  isNext: boolean;
  projectData?: ISelectOption[];
  projectIdsData?: string[];
  customFieldTypeData?: string;
  isMandatoryData?: boolean;
  isSearchableData?: boolean;
  customOptionsData?: CustomOptionSchema[];
};

export type CustomOptionSchema = {
  id?: string;
  custom_field_id: string,
  option: string,
  is_enable: boolean,
} 

const initialCustomFieldTypes = customFieldType.map(type => ({
  label: type.label,
  value: type.value,
  enabled: false,
}));

export const FormCustomField: React.FC<ICustomFieldProps> = ({
  projectIdsData,
  customFieldTypeData,
  projectData,
  isNext,
  isMandatoryData,
  isSearchableData,
  customOptionsData,
}) => {
  const form = useFormContext();
  const moduleTypeValue = form.watch('module_type');

  const selectAll = () => {
    const data = projectData?.map(option => option.value);
    form.setValue('project_ids', data);
  };

  const deselectAll = () => {
    form.setValue('project_ids', []);
  };

  const [showCustomFields, setShowCustomFields] = useState(true);

  const [customFieldTypes, setCustomFieldTypes] = useState(
    initialCustomFieldTypes,
  );

  useEffect(() => {
    if (moduleTypeValue === ModuleType.ISSUES) {
      const newCustomFieldTypes = initialCustomFieldTypes.slice(0, -1);
      setCustomFieldTypes(newCustomFieldTypes);
    } else {
      setCustomFieldTypes(initialCustomFieldTypes);
    }

    setShowCustomFields(moduleTypeValue !== ModuleType.TEST_RUN);
  }, [moduleTypeValue]);

  useEffect(() => {
    const customFieldTypeDB = customFieldTypes.map((customFieldType) => {
      if (customFieldType.value === customFieldTypeData) {
        return { ...customFieldType, enabled: true };
      } else {
        return { ...customFieldType, enabled: false };
      }
    });

    setCustomFieldTypes(customFieldTypeDB);
  }, []);

  const toggleEnabled = (index: number) => {
    const newCustomFieldTypes = customFieldTypes.map((customFieldType, idx) => {
      if (idx === index) {
        return { ...customFieldType, enabled: !customFieldType.enabled };
      } else {
        return { ...customFieldType, enabled: false };
      }
    });
    setCustomFieldTypes(newCustomFieldTypes);
  };

  const selectedCustomFieldTypes = customFieldTypes
    .filter(customFieldTypes => customFieldTypes.enabled)
    .map(customFieldTypes => customFieldTypes.value)
    .join(',');

  form.setValue('custom_field_type', selectedCustomFieldTypes);

  return !isNext ? (
    <div className="flex flex-col gap-4 mt-2">
      <SelectField
        name="module_type"
        label="Select Module Type"
        placeholder="Module Type"
        options={moduleTypeOptions}
        hasRequiredAsterisk
      />
      <SelectMultipleField
        name="project_ids"
        defaultValue={projectIdsData}
        label="Select Projects"
        placeholder="Project"
        options={projectData}
        isSearchable={true}
        hasRequiredAsterisk
      />

      <div className="flex justify-center gap-2">
        <OutlineButton icon={<FaPlus />} onClick={selectAll}>
          Select All
        </OutlineButton>
        <OutlineButton icon={<FaMinus />} onClick={deselectAll}>
          Deselect All
        </OutlineButton>
      </div>

      {showCustomFields && (
        <>
          <div className="after:content-['*'] after:text-red-400 after:text-sm">
            Custom Fields Type
          </div>

          <InputField
            name="custom_field_type"
            defaultValue={selectedCustomFieldTypes}
            type="hidden"
            key="custom_field_type"
          />

          {customFieldTypes.map((field, index) => (
            <CustomFieldInput
              enabled={field.enabled}
              label={field.label}
              key={field.label}
              onClick={() => toggleEnabled(index)}
            />
          ))}
        </>
      )};
    </div>
  ) : (
    <CustomFieldOption
      customFieldTypes={selectedCustomFieldTypes}
      moduleTypeValue={moduleTypeValue}
      isMandatoryData={isMandatoryData}
      isSearchableData={isSearchableData}
      customOptionsData={customOptionsData}
    />
  );
};
